import { useState } from 'react';
import { User, Wallet, Target, DollarSign } from 'lucide-react';
import { Game } from '../types';

interface FormData {
  xHandle: string;
  xrplAddress: string;
  tieBreakTotalScore: string;
  picks: Record<string, 'home' | 'away'>;
}

interface PickFormProps {
  formData: FormData;
  onFormDataChange: (data: FormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
  availableGamesCount: number;
  disabled: boolean;
  finalGame?: Game | null;
}

const PickForm = ({ 
  formData, 
  onFormDataChange, 
  onSubmit, 
  submitting, 
  availableGamesCount, 
  disabled,
  finalGame 
}: PickFormProps) => {
  const [showForm, setShowForm] = useState(false);
  
  const handleInputChange = (field: keyof FormData, value: string) => {
    onFormDataChange({
      ...formData,
      [field]: value
    });
  };

  const picksCount = Object.keys(formData.picks).length;
  const isFormValid = formData.xHandle && formData.xrplAddress && formData.tieBreakTotalScore && picksCount === availableGamesCount;

  if (disabled) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Submit Your Entry</h2>
          <p className="text-sm text-gray-600 mt-1">
            Picks: {picksCount}/{availableGamesCount} • Entry Fee: 1000 $NUTS
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn btn-primary"
            disabled={picksCount < availableGamesCount}
          >
            Enter Contest
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={onSubmit} className="space-y-6">
          {/* User Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="xHandle" className="block text-sm font-medium text-gray-700 mb-2">
                <User className="inline h-4 w-4 mr-1" />
                X Handle
              </label>
              <input
                type="text"
                id="xHandle"
                placeholder="@yourhandle"
                value={formData.xHandle}
                onChange={(e) => handleInputChange('xHandle', e.target.value)}
                className="input"
                required
              />
            </div>

            <div>
              <label htmlFor="xrplAddress" className="block text-sm font-medium text-gray-700 mb-2">
                <Wallet className="inline h-4 w-4 mr-1" />
                XRPL Address
              </label>
              <input
                type="text"
                id="xrplAddress"
                placeholder="rXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                value={formData.xrplAddress}
                onChange={(e) => handleInputChange('xrplAddress', e.target.value)}
                className="input"
                required
              />
            </div>
          </div>

          {/* Tie-breaker */}
          {finalGame && (
            <div>
              <label htmlFor="tieBreakTotalScore" className="block text-sm font-medium text-gray-700 mb-2">
                <Target className="inline h-4 w-4 mr-1" />
                Tie-breaker: Total Combined Score
              </label>
              <div className="mb-2 text-sm text-gray-600">
                {finalGame.awayTeam} @ {finalGame.homeTeam}
              </div>
              <input
                type="number"
                id="tieBreakTotalScore"
                placeholder="220"
                min="0"
                max="300"
                value={formData.tieBreakTotalScore}
                onChange={(e) => handleInputChange('tieBreakTotalScore', e.target.value)}
                className="input max-w-xs"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter your prediction for the total combined score (both teams)
              </p>
            </div>
          )}

          {/* Entry Fee Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-start">
              <DollarSign className="h-5 w-5 text-green-500 mt-0.5 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-gray-900">Entry Details</h4>
                <ul className="mt-1 text-xs text-gray-600 space-y-1">
                  <li>• Entry fee: 1000 $NUTS</li>
                  <li>• Payouts: 1st, 2nd, and 3rd place</li>
                  <li>• Tie-breaker: Total score of final game</li>
                  <li>• Payment via Xaman wallet</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-between pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormValid || submitting}
              className="btn btn-primary"
            >
              {submitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Entry...
                </div>
              ) : (
                'Submit Entry & Pay'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PickForm;
